/**
 * Analytics Event Tracking
 *
 * Centralized analytics tracking for PostHog.
 * All events should be tracked through this module for consistency.
 *
 * Event Naming Convention:
 * - Use snake_case for event names
 * - Use past tense for completed actions (e.g., 'invitation_created')
 * - Use present tense for ongoing states (e.g., 'builder_editing')
 */

import { trackEvent } from '@/providers/PostHogProvider'

// ============================================================================
// AUTHENTICATION EVENTS
// ============================================================================

export const analytics = {
  // -------------------------------------------------------------------------
  // Auth Events (handled in auth store, but can be called directly if needed)
  // -------------------------------------------------------------------------
  auth: {
    loginStarted: (method: 'email_otp' | 'phone_otp' | 'google') => {
      trackEvent('login_started', { method })
    },

    otpRequested: (type: 'email' | 'phone') => {
      trackEvent('otp_requested', { type })
    },

    otpResent: (type: 'email' | 'phone') => {
      trackEvent('otp_resent', { type })
    },

    signupCompleted: (method: 'email_otp' | 'phone_otp' | 'google') => {
      trackEvent('signup_completed', { method })
    },
  },

  // -------------------------------------------------------------------------
  // Builder Events
  // -------------------------------------------------------------------------
  builder: {
    started: () => trackEvent('builder_started'),

    opened: (eventId?: string, templateId?: string) =>
      trackEvent('builder_opened', {
        event_id: eventId,
        template_id: templateId,
        is_new: !eventId,
      }),

    templateSelected: (
      templateId: string,
      templateName?: string,
      category?: string
    ) =>
      trackEvent('template_selected', {
        templateId,
        templateName: templateName || templateId,
        category: category || 'default',
      }),

    stepCompleted: (step: 'details' | 'design' | 'rsvp_settings' | 'preview') =>
      trackEvent('builder_step_completed', { step }),

    sectionEdited: (sectionType: string) =>
      trackEvent('builder_section_edited', { section_type: sectionType }),

    sectionOpened: (sectionType: string) =>
      trackEvent('builder_section_opened', { section_type: sectionType }),

    imageUploaded: (sectionType: string, imageCount: number = 1) =>
      trackEvent('builder_image_uploaded', {
        section_type: sectionType,
        image_count: imageCount,
      }),

    saved: (
      invitationId: string,
      hasEventDate: boolean,
      isAutoSave: boolean = false
    ) => {
      trackEvent('invitation_saved', { invitationId, hasEventDate })
      if (!isAutoSave) {
        trackEvent('builder_saved', {
          event_id: invitationId,
          is_auto_save: isAutoSave,
        })
      }
    },

    previewOpened: (eventId?: string) =>
      trackEvent('builder_preview_opened', { event_id: eventId }),

    exited: (eventId?: string, hasUnsavedChanges: boolean = false) =>
      trackEvent('builder_exited', {
        event_id: eventId,
        has_unsaved_changes: hasUnsavedChanges,
      }),

    publishFlowStarted: (invitationId: string) =>
      trackEvent('builder_publish_flow_started', {
        invitation_id: invitationId,
      }),

    hostingDurationSelected: (days: number, price: number) =>
      trackEvent('builder_hosting_duration_selected', { days, price }),

    hostingAddedToCart: (invitationId: string, days: number, price: number) =>
      trackEvent('builder_hosting_added_to_cart', {
        invitation_id: invitationId,
        days,
        price,
      }),
  },

  // -------------------------------------------------------------------------
  // Invitation Events
  // -------------------------------------------------------------------------
  invitation: {
    created: (eventId: string, templateId?: string) =>
      trackEvent('invitation_created', {
        event_id: eventId,
        template_id: templateId,
      }),

    published: (invitationId: string) =>
      trackEvent('invitation_published', { invitationId }),

    unpublished: (eventId: string) =>
      trackEvent('invitation_unpublished', { event_id: eventId }),

    deleted: (eventId: string) =>
      trackEvent('invitation_deleted', { event_id: eventId }),

    duplicated: (originalEventId: string, newEventId: string) =>
      trackEvent('invitation_duplicated', {
        original_event_id: originalEventId,
        new_event_id: newEventId,
      }),

    shared: (
      invitationId: string,
      method: 'copy_link' | 'email' | 'sms' | 'social' | 'qr'
    ) => trackEvent('invitation_shared', { invitationId, method }),

    linkCopied: (eventId: string) =>
      trackEvent('invitation_link_copied', { event_id: eventId }),

    viewed: (eventId: string) =>
      trackEvent('invitation_viewed', { event_id: eventId }),

    detailsViewed: (eventId: string) =>
      trackEvent('invitation_details_viewed', { event_id: eventId }),
  },

  // -------------------------------------------------------------------------
  // RSVP Events
  // -------------------------------------------------------------------------
  rsvp: {
    submitted: (
      eventId: string,
      response: 'yes' | 'no' | 'maybe',
      guestCount: number = 1
    ) => {
      trackEvent('rsvp_submitted', {
        event_id: eventId,
        response,
        guest_count: guestCount,
      })
    },

    updated: (eventId: string, newResponse: 'yes' | 'no' | 'maybe') => {
      trackEvent('rsvp_updated', {
        event_id: eventId,
        new_response: newResponse,
      })
    },

    listViewed: (eventId: string) => {
      trackEvent('rsvp_list_viewed', { event_id: eventId })
    },

    exported: (eventId: string, format: 'csv' | 'excel') => {
      trackEvent('rsvp_exported', {
        event_id: eventId,
        format,
      })
    },
  },

  // -------------------------------------------------------------------------
  // Billing & Payment Events
  // -------------------------------------------------------------------------
  billing: {
    pricingViewed: () => {
      trackEvent('pricing_viewed')
    },

    checkoutStarted: (itemCount: number, subtotal: number) => {
      trackEvent('checkout_started', { item_count: itemCount, subtotal })
    },

    paymentMethodSelected: (
      method: 'card' | 'paypal' | 'apple_pay' | 'google_pay'
    ) => {
      trackEvent('payment_method_selected', { method })
    },

    paymentCompleted: (
      orderId: string,
      total: number,
      paymentMethod: string
    ) => {
      trackEvent('payment_completed', {
        order_id: orderId,
        total,
        payment_method: paymentMethod,
      })
    },

    paymentFailed: (error?: string) => {
      trackEvent('payment_failed', { error })
    },

    paymentMethodAdded: (type: 'card' | 'paypal') => {
      trackEvent('payment_method_added', { type })
    },

    paymentMethodRemoved: (type: 'card' | 'paypal') => {
      trackEvent('payment_method_removed', { type })
    },
  },

  // -------------------------------------------------------------------------
  // Settings Events
  // -------------------------------------------------------------------------
  settings: {
    viewed: (section?: string) => {
      trackEvent('settings_viewed', { section })
    },

    updated: (setting: string, value: unknown) => {
      trackEvent('settings_updated', {
        setting,
        // Don't log sensitive values
        value: typeof value === 'boolean' ? value : 'changed',
      })
    },

    themeChanged: (theme: 'light' | 'dark' | 'system') => {
      trackEvent('theme_changed', { theme })
    },

    languageChanged: (language: 'en' | 'ko') => {
      trackEvent('language_changed', { language })
    },

    notificationsToggled: (type: string, enabled: boolean) => {
      trackEvent('notifications_toggled', { type, enabled })
    },

    accountDeletionRequested: () => {
      trackEvent('account_deletion_requested')
    },

    accountDeletionCancelled: () => {
      trackEvent('account_deletion_cancelled')
    },
  },

  // -------------------------------------------------------------------------
  // Account Events
  // -------------------------------------------------------------------------
  account: {
    profileViewed: () => {
      trackEvent('profile_viewed')
    },

    profileUpdated: (fields: string[]) => {
      trackEvent('profile_updated', { fields_updated: fields })
    },

    avatarUploaded: () => {
      trackEvent('avatar_uploaded')
    },
  },

  // -------------------------------------------------------------------------
  // Notification Events
  // -------------------------------------------------------------------------
  notifications: {
    viewed: () => {
      trackEvent('notifications_viewed')
    },

    clicked: (notificationType: string) => {
      trackEvent('notification_clicked', { type: notificationType })
    },

    markedAsRead: (count: number = 1) => {
      trackEvent('notifications_marked_read', { count })
    },

    cleared: () => {
      trackEvent('notifications_cleared')
    },
  },

  // -------------------------------------------------------------------------
  // Navigation Events
  // -------------------------------------------------------------------------
  navigation: {
    menuOpened: () => {
      trackEvent('menu_opened')
    },

    ctaClicked: (ctaName: string, location: string) => {
      trackEvent('cta_clicked', { cta_name: ctaName, location })
    },

    externalLinkClicked: (url: string, context: string) => {
      trackEvent('external_link_clicked', {
        url,
        context,
      })
    },
  },

  // -------------------------------------------------------------------------
  // Page View Events
  // -------------------------------------------------------------------------
  page: {
    homepageViewed: (
      referrer?: string,
      utmSource?: string,
      utmCampaign?: string
    ) => {
      trackEvent('homepage_viewed', {
        referrer: referrer || '',
        utm_source: utmSource || '',
        utm_campaign: utmCampaign || '',
      })
    },

    designsPageViewed: (designCount: number) => {
      trackEvent('designs_page_viewed', { design_count: designCount })
    },

    designPreviewed: (designId: string, designName: string) => {
      trackEvent('design_previewed', {
        design_id: designId,
        design_name: designName,
      })
    },
  },

  // -------------------------------------------------------------------------
  // Error Events (supplement to Sentry)
  // -------------------------------------------------------------------------
  error: {
    displayed: (errorType: string, context?: string) => {
      trackEvent('error_displayed', {
        error_type: errorType,
        context,
      })
    },

    retryClicked: (context: string) => {
      trackEvent('error_retry_clicked', { context })
    },
  },

  // -------------------------------------------------------------------------
  // Feature Discovery
  // -------------------------------------------------------------------------
  feature: {
    discovered: (
      featureName: string,
      method: 'tooltip' | 'tour' | 'natural'
    ) => {
      trackEvent('feature_discovered', {
        feature_name: featureName,
        method,
      })
    },

    tooltipViewed: (tooltipId: string) => {
      trackEvent('tooltip_viewed', { tooltip_id: tooltipId })
    },
  },

  // -------------------------------------------------------------------------
  // Search & Discovery
  // -------------------------------------------------------------------------
  search: {
    performed: (query: string, resultsCount: number, context: string) => {
      trackEvent('search_performed', {
        query_length: query.length,
        results_count: resultsCount,
        context,
      })
    },

    resultClicked: (resultType: string, position: number) => {
      trackEvent('search_result_clicked', { result_type: resultType, position })
    },
  },

  // -------------------------------------------------------------------------
  // Engagement & Retention
  // -------------------------------------------------------------------------
  engagement: {
    sessionStarted: () => {
      trackEvent('session_started', {
        referrer: typeof document !== 'undefined' ? document.referrer : '',
      })
    },

    timeOnPage: (pageName: string, seconds: number) => {
      trackEvent('time_on_page', { page_name: pageName, seconds })
    },

    featureUsed: (featureName: string, context?: string) => {
      trackEvent('feature_used', { feature_name: featureName, context })
    },
  },

  // -------------------------------------------------------------------------
  // Conversion Funnel
  // -------------------------------------------------------------------------
  funnel: {
    stepCompleted: (
      funnelName: string,
      stepNumber: number,
      stepName: string
    ) => {
      trackEvent('funnel_step_completed', {
        funnel_name: funnelName,
        step_number: stepNumber,
        step_name: stepName,
      })
    },

    abandoned: (funnelName: string, lastStep: number, reason?: string) => {
      trackEvent('funnel_abandoned', {
        funnel_name: funnelName,
        last_step: lastStep,
        reason,
      })
    },
  },

  // -------------------------------------------------------------------------
  // Cart Events
  // -------------------------------------------------------------------------
  cart: {
    opened: (itemCount: number, subtotal: number) => {
      trackEvent('cart_opened', { item_count: itemCount, subtotal })
    },

    closed: (itemCount: number) => {
      trackEvent('cart_closed', { item_count: itemCount })
    },

    itemRemoved: (
      productId: string,
      productType: string,
      productName: string
    ) => {
      trackEvent('cart_item_removed', {
        product_id: productId,
        product_type: productType,
        product_name: productName,
      })
    },

    cleared: (itemCount: number) => {
      trackEvent('cart_cleared', { item_count: itemCount })
    },

    promoApplied: (code: string, discountAmount: number) => {
      trackEvent('promo_code_applied', {
        code,
        discount_amount: discountAmount,
      })
    },

    promoFailed: (code: string, error: string) => {
      trackEvent('promo_code_failed', { code, error })
    },

    promoRemoved: (code: string) => {
      trackEvent('promo_code_removed', { code })
    },
  },

  // -------------------------------------------------------------------------
  // Checkout Events
  // -------------------------------------------------------------------------
  checkout: {
    started: (invitationId: string | null, cartValue: number) =>
      trackEvent('checkout_started', { invitationId, cartValue }),

    planSelected: (planId: string, planName: string, price: number) =>
      trackEvent('plan_selected', { planId, planName, price }),

    paymentInfoEntered: () => trackEvent('payment_info_entered'),

    pageViewed: (
      itemCount: number,
      subtotal: number,
      isHostingCheckout: boolean = false
    ) => {
      trackEvent('checkout_page_viewed', {
        item_count: itemCount,
        subtotal,
        is_hosting_checkout: isHostingCheckout,
      })
    },

    paymentInitiated: (paymentMethod: 'stripe' | 'paypal', amount: number) => {
      trackEvent('checkout_payment_initiated', {
        payment_method: paymentMethod,
        amount,
      })
    },

    paymentSucceeded: (
      orderId: string,
      total: number,
      paymentMethod: string
    ) => {
      trackEvent('checkout_payment_succeeded', {
        order_id: orderId,
        total,
        payment_method: paymentMethod,
      })
    },

    paymentFailed: (paymentMethod: string, error: string) => {
      trackEvent('checkout_payment_failed', {
        payment_method: paymentMethod,
        error,
      })
    },

    paymentCancelled: (paymentMethod: string) => {
      trackEvent('checkout_payment_cancelled', {
        payment_method: paymentMethod,
      })
    },

    resultViewed: (
      status: 'success' | 'failed' | 'processing' | 'cancelled',
      orderId?: string
    ) => {
      trackEvent('checkout_result_viewed', { status, order_id: orderId })
    },
  },

  // -------------------------------------------------------------------------
  // Image Library Events (Phase 3)
  // -------------------------------------------------------------------------
  imageLibrary: {
    opened: (context: string, tab: string) => {
      trackEvent('image_library_opened', { context, tab })
    },

    tabSwitched: (fromTab: string, toTab: string) => {
      trackEvent('image_library_tab_switched', {
        from_tab: fromTab,
        to_tab: toTab,
      })
    },

    imageSelected: (imageId: string, source: string) => {
      trackEvent('image_selected', { image_id: imageId, source })
    },
  },

  // -------------------------------------------------------------------------
  // Image Editor Events (Phase 3)
  // -------------------------------------------------------------------------
  imageEditor: {
    opened: (imageId: string) => {
      trackEvent('image_editor_opened', { image_id: imageId })
    },

    filterApplied: (filterType: string) => {
      trackEvent('image_filter_applied', { filter_type: filterType })
    },

    cropped: (aspectRatio: string) => {
      trackEvent('image_cropped', { aspect_ratio: aspectRatio })
    },

    saved: (imageId: string, edits: string[]) => {
      trackEvent('image_edited_saved', { image_id: imageId, edits_made: edits })
    },
  },

  // -------------------------------------------------------------------------
  // Gallery Events (Phase 3)
  // -------------------------------------------------------------------------
  gallery: {
    viewed: (invitationId: string, photoCount: number) => {
      trackEvent('gallery_viewed', {
        invitation_id: invitationId,
        photo_count: photoCount,
      })
    },

    photoDownloaded: (invitationId: string, count: number) => {
      trackEvent('gallery_photo_downloaded', {
        invitation_id: invitationId,
        count,
      })
    },

    photoModerated: (
      invitationId: string,
      action: 'hide' | 'show' | 'delete'
    ) => {
      trackEvent('gallery_photo_moderated', {
        invitation_id: invitationId,
        action,
      })
    },
  },

  // -------------------------------------------------------------------------
  // Broadcast Events (Phase 3)
  // -------------------------------------------------------------------------
  broadcast: {
    sent: (
      invitationId: string,
      recipientCount: number,
      messageLength: number
    ) => {
      trackEvent('broadcast_sent', {
        invitation_id: invitationId,
        recipient_count: recipientCount,
        message_length: messageLength,
      })
    },
  },

  // -------------------------------------------------------------------------
  // Cloud Export Events (Phase 3)
  // -------------------------------------------------------------------------
  cloudExport: {
    started: (provider: string, photoCount: number) => {
      trackEvent('cloud_export_started', { provider, photo_count: photoCount })
    },

    completed: (provider: string, photoCount: number, failed: number = 0) => {
      trackEvent('cloud_export_completed', {
        provider,
        photo_count: photoCount,
        failed_count: failed,
      })
    },

    failed: (provider: string, error: string) => {
      trackEvent('cloud_export_failed', { provider, error })
    },
  },

  // -------------------------------------------------------------------------
  // Keepsakes Events (Phase 4)
  // -------------------------------------------------------------------------
  keepsakes: {
    pageViewed: () => {
      trackEvent('keepsakes_page_viewed')
    },

    categoryViewed: (categorySlug: string, categoryName: string) => {
      trackEvent('keepsakes_category_viewed', {
        category_slug: categorySlug,
        category_name: categoryName,
      })
    },

    productViewed: (
      productId: string,
      productName: string,
      price: number,
      category: string
    ) => {
      trackEvent('keepsake_product_viewed', {
        product_id: productId,
        product_name: productName,
        price,
        category,
      })
    },

    addedToCart: (
      productId: string,
      productName: string,
      quantity: number,
      price: number
    ) => {
      trackEvent('keepsake_added_to_cart', {
        product_id: productId,
        product_name: productName,
        quantity,
        price,
      })
    },
  },

  // -------------------------------------------------------------------------
  // Generic tracking for custom events
  // -------------------------------------------------------------------------
  track: trackEvent,
}

export default analytics
